import type { Nullable } from '@iheartjane/types';
import get from 'lodash-es/get';
import isEmpty from 'lodash-es/isEmpty';
import type { PropsWithChildren } from 'react';
import { createContext, useCallback, useMemo } from 'react';

import { useEnsureContextWithinProvider } from './hooks/useEnsureContextWithinProvider';

interface ComplianceLanguageContext {
  getWord: (key: string) => string | null;
  languageSettings?: Nullable<Record<string, string>>;
}

const ComplianceLanguageContext =
  createContext<ComplianceLanguageContext | null>(null);

export const ComplianceLanguageProvider = ({
  children,
  languageSettings,
}: PropsWithChildren<Pick<ComplianceLanguageContext, 'languageSettings'>>) => {
  const getWord = useCallback(
    (key: string) => {
      const word = get(languageSettings, key, null);
      return !isEmpty(word) ? word : null;
    },
    [languageSettings]
  );

  const contextValue = useMemo(
    () => ({ getWord, languageSettings }),
    [getWord, languageSettings]
  );

  return (
    <ComplianceLanguageContext.Provider value={contextValue}>
      {children}
    </ComplianceLanguageContext.Provider>
  );
};

export const useComplianceLanguage = () =>
  useEnsureContextWithinProvider(
    ComplianceLanguageContext,
    'ComplianceLanguage'
  );
